import { defineStore } from "pinia";
import { createStorefrontApiClient } from '@shopify/storefront-api-client';
import { productFields, cartFields, influencerFields, variantFields } from '@/settings/shopify';

export const useShopify = defineStore({
  id: 'shopify',

  state: () => ({
    client: null,
    cartId: null,
    cartLines: 0,
    cartValue: 0
  }),

  persist: {
    pick: ['cartId', 'cartLines', 'cartValue'],
  },

  actions: {
    connect() {
      this.client = createStorefrontApiClient({
        storeDomain: import.meta.env.VITE_SHOPIFY_STOREFRONT_DOMAIN,
        publicAccessToken: import.meta.env.VITE_SHOPIFY_STOREFRONT_ACCESS_TOKEN,
        apiVersion: import.meta.env.VITE_SHOPIFY_STOREFRONT_API_VERSION
      });
    },

    updateCart(cart) {
      let lineCount = 0;
      if (cart.lines.edges.length > 0) {
        cart.lines.edges.forEach(line => {
          lineCount += line.node.quantity;
        });
      }
      this.cartLines = lineCount;
      this.cartValue = cart.cost.totalAmount ?? 0;
    },

    resetCart() {
      this.cartId = null,
      this.cartLines = 0,
      this.cartValue = 0
    },

    async fetchQuery(query, args, queryName = '') {
      return new Promise((resolve, reject) => {
        this.client.request(query, args)
          .then((response) => {
            if (response.errors) {
              if (response.errors.graphQLErrors) {
                response.errors.graphQLErrors.forEach(error => {
                  console.error(error.message, queryName);
                });
              }
              return reject(response.errors);
            }

            return resolve(response.data);
          })
          .catch((error) => {
            return reject(error);
          });
      });
    },

    async fetchInfluencer(influencerId, includeProducts = true, imgWidth = 250, imgHeight = 250) {
      let fields = influencerFields;
      if (includeProducts) {
        fields += `products(first: 99) {
          edges {
            node {
              ${productFields}
            }
          }
        }`;
      }

      const query = `query getInfluencer($influencerId: ID!, $imgWidth: Int, $imgHeight: Int) {
        collection(id: $influencerId) {
          ${fields}
        }
      }
      `;
      return this.fetchQuery(query, { variables: { influencerId, imgWidth, imgHeight } }, 'fetchInfluencer');
    },

    async fetchInfluencerByHandle(influencerHandle, includeProducts = true, imgWidth = 250, imgHeight = 250) {
      let fields = influencerFields;
      if (includeProducts) {
        fields += `products(first: 99) {
          edges {
            node {
              ${productFields}
            }
          }
        }`;
      }

      const query = `query getInfluencer($influencerHandle: String!, $imgWidth: Int, $imgHeight: Int) {
        collection(handle: $influencerHandle) {
          ${fields}
        }
      }
      `;
      return this.fetchQuery(query, { variables: { influencerHandle, imgWidth, imgHeight } }, 'fetchInfluencer');
    },

    async fetchInfluencers(includeProducts = true, imgWidth = 250, imgHeight = 250, sorted = true) {
      let fields = influencerFields;
      if (includeProducts) {
        fields += `products(first: 99) {
          edges {
            node {
              ${productFields}
            }
          }
        }`;
      }

      const query = `query getInfluencers($imgWidth: Int, $imgHeight: Int) {
        collections(first: 99) {
          edges {
            node {
              ${fields}
            }
          }
        }
      }`;

      if (!sorted) {
        return this.fetchQuery(query, { variables: { imgWidth, imgHeight } }, 'fetchInfluencers');
      } else {
        const unsorted = await this.fetchQuery(query, { variables: { imgWidth, imgHeight } }, 'fetchInfluencers');
        const open = unsorted.collections.edges.filter((influencer) => influencer.node.open.value === 'true');
        const closed = unsorted.collections.edges.filter((influencer) => influencer.node.open.value === 'false');
        return Promise.resolve(open.concat(closed));
      }
    },

    async fetchProductsForInfluencer(influencerId, imgWidth = 250, imgHeight = 250) {
      const query = `query getProducts($id: ID, $imgWidth: Int, $imgHeight: Int) {
        collection(id: $id) {
          products(first: 99) {
            edges {
              node {
                ${productFields}
              }
            }
          }
        }
      }`;
      return this.fetchQuery(query, { variables: { id: influencerId, imgWidth, imgHeight } }, 'fetchProductsForCollection');
    },

    async fetchProductById(productId) {
      const query = `query getProduct($id: ID) {
        product(id: $id) {
          ${productFields}
        }
      }`;

      return this.fetchQuery(query, { variables: { id: productId } }, 'fetchProductById');
    },

    async fetchProductByHandle(handle) {
      const query = `query getProduct($handle: String) {
        product(handle: $handle) {
          ${productFields}
        }
      }`;

      return this.fetchQuery(query, { variables: { handle } }, 'fetchProductById');
    },

    async fetchProducts() {
      const query = `query getProducts {
        products(first: 99) {
          edges {
            node {
              ${productFields}
            }
          }
        }
      }`;

      return this.fetchQuery(query, {}, 'fetchProducts');
    },

    async variantForOptions(productId, variants, imgWidth = 250, imgHeight = 250) {
      const query = `query getVariant($id: ID, $options: [SelectedOptionInput!]!, $imgWidth: Int, $imgHeight: Int) {
        product(id: $id) {
          variantBySelectedOptions(selectedOptions: $options) {
            ${variantFields}
          }
        }
      }`;

      return this.fetchQuery(query, { variables: { id: productId, options: variants, imgWidth, imgHeight } }, 'variantForOptions');
    },

    async fetchRelatedProducts(productId) {
      const query = `query productRecommendations($productId: ID!) {
        productRecommendations(productId: $productId) {
          ${productFields}
        }
      }`;

      return this.fetchQuery(query, { variables: { productId } }, 'fetchRelatedProducts');
    },

    async createCart(merchandiseId, quantity) {
      const mutation = `mutation createCart($merchandiseId: ID!, $quantity: Int!) {
        cartCreate(
          input: {
            lines: [
              {
                merchandiseId: $merchandiseId,
                quantity: $quantity
              }
            ]
          }
        ) {
          cart {
            id
            createdAt
            updatedAt
            checkoutUrl
          }
        }
      }`;

      return new Promise((resolve, reject) => {
        this.fetchQuery(mutation, { variables: { merchandiseId, quantity } }, 'addToCart')
          .then((result) => {
            if (result.errors) {
              return reject(result.errors);
            }

            this.cartId = result.cartCreate.cart.id;
            // console.log('set cartId to ' + this.cartId);

            return resolve(result);
          });
      });
    },

    async fastCheckout(merchandiseId, quantity) {
      const mutation = `mutation createCart($merchandiseId: ID!, $quantity: Int!) {
        cartCreate(
          input: {
            lines: [
              {
                merchandiseId: $merchandiseId,
                quantity: $quantity
              }
            ]
          }
        ) {
          cart {
            id
            createdAt
            updatedAt
            checkoutUrl
          }
        }
      }`;

      return new Promise((resolve, reject) => {
        this.fetchQuery(mutation, { variables: { merchandiseId, quantity } }, 'addToCart')
          .then((result) => {
            if (result.errors) {
              return reject(result.errors);
            }

            return resolve(result.cartCreate.cart.checkoutUrl);
          });
      });
    },

    async addToCart(merchandiseId, quantity) {
      if (!this.cartId) {
        return this.createCart(merchandiseId, quantity);
      }

      const mutation = `mutation addToCart($merchandiseId: ID!, $quantity: Int!) {
         cartLinesAdd(
          cartId: "${this.cartId}",
          lines: [
            {
              merchandiseId: $merchandiseId,
              quantity: $quantity
            }
          ]
        ) {
          cart {
            id
            createdAt
            updatedAt
          }
        }
      }`;

      return this.fetchQuery(mutation, { variables: { merchandiseId, quantity } }, 'addLinesToCart');
    },

    async removeLineFromCart(lineId, imgWidth = 400, imgHeight = 400) {
      const mutation = `mutation removeLineFromCart($cartId: ID!, $lineIds: [ID!]!, $imgWidth: Int, $imgHeight: Int) {
        cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
          cart ${cartFields}
        }
      }`;

      return this.fetchQuery(mutation, { variables: { cartId: this.cartId, lineIds: [lineId], imgWidth, imgHeight } }, 'removeLineFromCart');
    },

    async changeLineQuantity(lineId, quantity, imgWidth = 400, imgHeight = 400) {
      const mutation = `mutation changeLineQuantity($cartId: ID!, $lineId: ID!, $quantity: Int!, $imgWidth: Int, $imgHeight: Int) {
        cartLinesUpdate(
          cartId: $cartId,
          lines: [
            {
              id: $lineId,
              quantity: $quantity
            }
          ]
        ) {
          cart ${cartFields}
        }
      }`;

      return this.fetchQuery(mutation, { variables: { cartId: this.cartId, lineId: lineId, quantity, imgWidth, imgHeight } }, 'changeLineQuantity');
    },

    async fetchCart(cartId, imgWidth = 400, imgHeight = 400) {
      const query = `query fetchCart($cartId: ID!, $imgWidth: Int, $imgHeight: Int) {
        cart(id: $cartId) ${cartFields}
      }`;

      return this.fetchQuery(query, { variables: { cartId, imgWidth, imgHeight } }, 'fetchCart');
    },

    async searchProducts(searchWord) {
      const query = `query searchProducts($searchWord: String!) {
        search(query: $searchWord, first: 99, types: PRODUCT) {
          edges {
            node {
              ... on Product {
                id
              }
            }
          }
        }
      }`;

      return this.fetchQuery(query, { variables: { searchWord } }, 'searchProducts');
    }
  }
});